import { Box, Card, CardBody, CardHeader, Heading, Skeleton } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface BudgetBarChartProps {
  title: string
  labels: string[]
  income: number[]
  expenses: number[]
  isLoading?: boolean
}

export function BudgetBarChart({
  title,
  labels,
  income,
  expenses,
  isLoading = false,
}: BudgetBarChartProps) {
  const colors = useThemeColors()

  const chartData = labels.map((label, index) => ({
    label,
    income: income[index] ?? 0,
    expenses: expenses[index] ?? 0,
  }))

  return (
    <Card border="1px solid" borderColor={colors.border} borderRadius="16px" bg={colors.cardBg} overflow="hidden">
      <CardHeader pb={2}>
        <Heading size="md">{title}</Heading>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Box h="260px" px={2} display="flex" alignItems="flex-end" justifyContent="space-between" gap={1}>
            {[64, 88, 52, 96, 72, 110, 58, 84].map((h, i) => (
              <Skeleton key={i} flex="1" maxW="12%" h={`${h}px`} borderRadius="md" />
            ))}
          </Box>
        ) : chartData.length === 0 ? (
          <Box h="260px" display="flex" alignItems="center" justifyContent="center" color={colors.textMuted}>
            No chart data for this range.
          </Box>
        ) : (
          <Box h="260px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.12} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  angle={-35}
                  textAnchor="end"
                  height={70}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', borderColor: '#E2E8F0' }}
                  labelStyle={{ color: '#1A202C' }}
                  formatter={(value: number, name: string) => {
                    const formatted = value.toLocaleString()
                    return [`P ${formatted}`, name === 'Income' ? 'Budget' : 'Expenses']
                  }}
                />
                <Bar dataKey="income" name="Income" fill="#3182CE" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#E53E3E" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardBody>
    </Card>
  )
}

